import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Upload, X, Video, ImagePlus, ClipboardEdit } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/config";
import { REQUEST_QUOTE_STARTING_PRICES, REQUEST_QUOTE_PRICE_DISCLAIMER } from "./BookLanding";

const SERVICE_LABELS = {
  tree_removal: "Tree Removal",
  stump_grinding: "Stump Grinding",
  sodding: "Sodding",
  outdoor_lighting: "Outdoor Lighting",
  landscaping: "Landscaping",
  custom_service: "Custom Service",
  bush_trimming: "Bush Trimming",
  mulching: "Mulching",
  gutter_cleaning: "Gutter Cleaning",
  pressure_washing: "Pressure Washing",
  junk_removal: "Junk Removal",
  weed_removal: "Weed Removal",
  rock_installation: "Rock Installation",
  // iter77: Legacy Home service keys that route through Request-a-Quote
  edging: "Edging",
  yard_cleanup: "Yard Clean-up",
  leaf_removal: "Leaf Removal",
  flower_planting: "Flower Planting",
  drainage_installation: "Drainage Installation",
  fence_repair: "Fence Repair",
  sprinkler_repair: "Sprinkler Repair",
};

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
const MAX_VIDEO_SECONDS = 60;
const MAX_PHOTOS = 10;
const PHOTO_MIMES = new Set(["image/jpeg", "image/jpg", "image/png", "image/heic", "image/heif"]);
const VIDEO_MIMES = new Set(["video/mp4", "video/quicktime", "video/webm", "video/x-m4v", "video/mpeg"]);

export default function RequestQuote() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const serviceKey = sp.get("service") || "custom_service";
  const [form, setForm] = useState({
    description: "",
    address: "", city: "", state: "FL", zip_code: "",
    name: "", email: "", phone: "",
    preferred_date: "",
  });
  const [photos, setPhotos] = useState([]);   // [{media_id, preview}]
  const [video, setVideo] = useState(null);   // {media_id, preview, seconds}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  const readFile = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });

  const probeVideoDuration = (dataUrl) => new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => resolve(v.duration || 0);
    v.onerror = () => resolve(0);
    v.src = dataUrl;
  });

  const onPhotoChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Up to ${MAX_PHOTOS} photos.`);
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        if (!PHOTO_MIMES.has(f.type)) {
          toast.error(`${f.name}: unsupported (JPG/PNG/HEIC only).`);
          continue;
        }
        if (f.size > MAX_PHOTO_BYTES) {
          toast.error(`${f.name}: over 10 MB.`);
          continue;
        }
        const dataUrl = await readFile(f);
        const { data } = await api.post("/quotes/upload/photo", { data_url: dataUrl });
        setPhotos((p) => [...p, { media_id: data.media_id, preview: dataUrl }]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const onVideoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    if (!VIDEO_MIMES.has(f.type)) {
      toast.error("Video must be MP4, MOV, or WebM.");
      return;
    }
    if (f.size > MAX_VIDEO_BYTES) {
      toast.error(`Video is ${Math.round(f.size / 1024 / 1024)} MB — max 50 MB.`);
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await readFile(f);
      const seconds = await probeVideoDuration(dataUrl);
      if (seconds > MAX_VIDEO_SECONDS + 2) {
        toast.error(`Video is ${Math.round(seconds)} s — max ${MAX_VIDEO_SECONDS} s.`);
        return;
      }
      const { data } = await api.post("/quotes/upload/video", {
        data_url: dataUrl,
        duration_seconds: seconds,
      });
      setVideo({ media_id: data.media_id, preview: dataUrl, seconds });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video upload failed");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (form.description.trim().length < 10) {
      toast.error("Please describe the job (at least 10 characters).");
      return;
    }
    if (!form.email && !form.phone) {
      toast.error("We need an email or phone to send the quote.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/quotes/request", {
        service_key: serviceKey,
        description: form.description.trim(),
        address: form.address || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        name: form.name || null,
        email: form.email || null,
        phone: form.phone || null,
        preferred_date: form.preferred_date || null,
        photo_media_ids: photos.map((p) => p.media_id),
        video_media_id: video?.media_id || null,
      });
      setSubmittedId(data.quote_id);
      toast.success("Quote request sent! We'll get back to you within 24 hours.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "object" ? d.message : (d || "Could not submit");
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  if (submittedId) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center" data-testid="quote-submitted">
        <div className="text-6xl mb-3">✅</div>
        <h1 className="text-2xl font-extrabold text-gray-900 font-[Outfit]">Quote request sent</h1>
        <p className="mt-2 text-sm text-gray-600">
          We&apos;ll review your request and send a detailed quote within 24 hours.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <Link to="/dashboard" className="inline-flex items-center justify-center gap-1 px-4 h-12 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-6" data-testid={`request-quote-${serviceKey}`}>
      <Link to="/book" className="inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-[0.14em] text-emerald-800 hover:underline mb-4">
        <ArrowLeft className="w-3 h-3" /> Back
      </Link>

      <div className="rounded-3xl bg-white border-2 border-amber-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-br from-amber-50 to-white p-5 border-b border-amber-100">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center">
              <ClipboardEdit className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-amber-800">Request a Quote</div>
              <div className="text-xl font-extrabold text-gray-900 font-[Outfit]">
                {SERVICE_LABELS[serviceKey] || "Custom Service"}
              </div>
              {/* iter83: Show a starting price + disclaimer so the customer
                  has realistic budget context before submitting. */}
              {REQUEST_QUOTE_STARTING_PRICES[serviceKey] && (
                <div
                  className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[11px] font-extrabold"
                  data-testid="rq-starting-price"
                >
                  {REQUEST_QUOTE_STARTING_PRICES[serviceKey]}
                </div>
              )}
            </div>
          </div>
          <p
            className="mt-3 text-[11px] text-amber-900/90 leading-relaxed font-medium"
            data-testid="rq-price-disclaimer"
          >
            {REQUEST_QUOTE_PRICE_DISCLAIMER}
          </p>
        </div>

        <div className="p-5 space-y-4">
          <TextArea
            label="Describe the job"
            placeholder="e.g. Large oak needs to come down; leaning slightly toward the fence. About 40 ft tall."
            value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            testid="rq-description"
          />

          <div className="grid grid-cols-2 gap-3">
            <Text label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="rq-name" />
            <Text label="Preferred date" type="date" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} testid="rq-date" />
            <Text label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="rq-email" />
            <Text label="Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="rq-phone" />
          </div>
          <Text label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="rq-address" />
          <div className="grid grid-cols-3 gap-3">
            <Text label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="rq-city" />
            <Text label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="rq-state" />
            <Text label="Zip" value={form.zip_code} onChange={(v) => setForm({ ...form, zip_code: v })} testid="rq-zip" />
          </div>

          {/* Photos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.14em] font-extrabold text-gray-600">
                Photos <span className="font-medium text-gray-400 normal-case tracking-normal">(up to {MAX_PHOTOS}, 10 MB each)</span>
              </div>
              <label className="cursor-pointer inline-flex items-center gap-1 px-3 h-8 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-extrabold">
                <ImagePlus className="w-3.5 h-3.5" /> Add
                <input type="file" accept="image/jpeg,image/png,image/heic,image/heif" multiple onChange={onPhotoChange} className="hidden" data-testid="rq-photo-input" />
              </label>
            </div>
            {photos.length > 0 && (
              <div className="grid grid-cols-4 gap-2" data-testid="rq-photo-grid">
                {photos.map((p, i) => (
                  <div key={p.media_id} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img src={p.preview} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setPhotos(photos.filter((x) => x.media_id !== p.media_id))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid={`rq-photo-remove-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Video */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.14em] font-extrabold text-gray-600">
                Video <span className="font-medium text-gray-400 normal-case tracking-normal">(up to 1, 60 s / 50 MB)</span>
              </div>
              {!video && (
                <label className="cursor-pointer inline-flex items-center gap-1 px-3 h-8 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-extrabold">
                  <Video className="w-3.5 h-3.5" /> Add
                  <input type="file" accept="video/mp4,video/quicktime,video/webm" onChange={onVideoChange} className="hidden" data-testid="rq-video-input" />
                </label>
              )}
            </div>
            {video && (
              <div className="relative rounded-lg overflow-hidden bg-gray-100" data-testid="rq-video-preview">
                <video src={video.preview} controls className="w-full max-h-48 object-contain bg-black" />
                <button onClick={() => setVideo(null)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid="rq-video-remove">
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="p-2 text-[11px] text-gray-500">{Math.round(video.seconds)} seconds</div>
              </div>
            )}
          </div>

          <Button
            onClick={submit}
            disabled={submitting || uploading}
            className="w-full h-13 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 text-white font-extrabold text-base shadow-lg"
            data-testid="rq-submit"
          >
            {submitting || uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? "Uploading…" : "Send Quote Request"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Text({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-600">{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-gray-200 focus:border-amber-400 outline-none text-sm"
        data-testid={testid}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.14em] font-extrabold text-gray-600">{label}</span>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={5}
        className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-gray-200 focus:border-amber-400 outline-none text-sm leading-relaxed"
        data-testid={testid}
      />
    </label>
  );
}
